import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { AllocationSheetScheduleService } from "services/allocation-sheet-schedule";
import { formatVndIntegerInput, parseVndIntegerInput } from "utils";
import { getPrintTemplate } from "./components/printTemplate";

const createRow = () => ({
  debitObject: "",
  account152Cost: "",
  account152Actual: "",
  account153Cost: "",
  account153Actual: "",
  account242: "",
});

const formatMonth2Digits = (value) => String(value || "").replace(/\D/g, "").slice(0, 2);
const normalizeMonth = (value) => {
  const raw = formatMonth2Digits(value);
  if (!raw) return "";
  return raw.padStart(2, "0");
};

const roundMoney = (value) => {
  const parsed = parseVndIntegerInput(value);
  return Number.isFinite(parsed) ? Math.round(parsed) : 0;
};

const fmtMoney = (value) => new Intl.NumberFormat("vi-VN").format(Math.round(Number(value) || 0));

export default function AllocationSheetScheduleDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();

  const today = new Date();

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [header, setHeader] = useState({
    unit: "",
    department: "",
    month: String(today.getMonth() + 1).padStart(2, "0"),
    year: String(today.getFullYear()),
    documentNumber: "",
  });

  const [rows, setRows] = useState([createRow()]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await AllocationSheetScheduleService.getById(id);
        const data = response.data ? response.data : response;

        setHeader({
          unit: data.unit || "",
          department: data.department || "",
          month: normalizeMonth(data.month || ""),
          year: data.year || "",
          documentNumber: data.documentNumber || "",
        });

        const mappedRows = (data.rows || []).map((row) => ({
          debitObject: row.debitObject || "",
          account152Cost: formatVndIntegerInput(row.account152Cost || ""),
          account152Actual: formatVndIntegerInput(row.account152Actual || ""),
          account153Cost: formatVndIntegerInput(row.account153Cost || ""),
          account153Actual: formatVndIntegerInput(row.account153Actual || ""),
          account242: formatVndIntegerInput(row.account242 || ""),
        }));

        setRows(mappedRows.length ? mappedRows : [createRow()]);
      } catch (error) {
        toast.error("Không thể tải dữ liệu bảng phân bổ");
      }
    })();
  }, [id]);

  const derivedRows = useMemo(() =>
    rows.map((row) => ({
      ...row,
      account152Cost: roundMoney(row.account152Cost),
      account152Actual: roundMoney(row.account152Actual),
      account153Cost: roundMoney(row.account153Cost),
      account153Actual: roundMoney(row.account153Actual),
      account242: roundMoney(row.account242),
    })),
  [rows]);

  const totals = useMemo(() => {
    return derivedRows.reduce(
      (acc, row) => ({
        account152Cost: acc.account152Cost + row.account152Cost,
        account152Actual: acc.account152Actual + row.account152Actual,
        account153Cost: acc.account153Cost + row.account153Cost,
        account153Actual: acc.account153Actual + row.account153Actual,
        account242: acc.account242 + row.account242,
        total:
          acc.total +
          row.account152Cost +
          row.account152Actual +
          row.account153Cost +
          row.account153Actual +
          row.account242,
      }),
      {
        account152Cost: 0,
        account152Actual: 0,
        account153Cost: 0,
        account153Actual: 0,
        account242: 0,
        total: 0,
      }
    );
  }, [derivedRows]);

  const setHeaderField = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const setRowField = (index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      let nextValue = value;
      if (
        [
          "account152Cost",
          "account152Actual",
          "account153Cost",
          "account153Actual",
          "account242",
        ].includes(field)
      ) {
        nextValue = formatVndIntegerInput(value);
      }
      next[index] = { ...next[index], [field]: nextValue };
      return next;
    });
  };

  const addRow = () => {
    setRows((prev) => [...prev, createRow()]);
  };

  const removeRow = (index) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const buildPayload = () => {
    const unit = String(header.unit || "").trim();
    const department = String(header.department || "").trim();
    const month = normalizeMonth(header.month);
    const year = String(header.year || "").trim();
    const documentNumber = String(header.documentNumber || "").trim();

    if (!unit) throw new Error("Đơn vị không được để trống");
    if (!department) throw new Error("Bộ phận không được để trống");
    if (!month) throw new Error("Tháng không được để trống");
    if (!year) throw new Error("Năm không được để trống");
    if (!documentNumber) throw new Error("Số bảng phân bổ không được để trống");

    const normalizedRows = derivedRows
      .map((row, index) => ({
        serialNumber: String(index + 1),
        debitObject: String(row.debitObject || "").trim(),
        account152Cost: row.account152Cost,
        account152Actual: row.account152Actual,
        account153Cost: row.account153Cost,
        account153Actual: row.account153Actual,
        account242: row.account242,
      }))
      .filter((row) => {
        const hasAmount =
          row.account152Cost > 0 ||
          row.account152Actual > 0 ||
          row.account153Cost > 0 ||
          row.account153Actual > 0 ||
          row.account242 > 0;
        return row.debitObject || hasAmount;
      });

    if (!normalizedRows.length) {
      throw new Error("Bảng phân bổ phải có ít nhất 1 dòng dữ liệu");
    }

    normalizedRows.forEach((row, index) => {
      if (!row.debitObject) {
        throw new Error(`Dòng ${index + 1}: Đối tượng sử dụng (Ghi Nợ các TK) không được để trống`);
      }
    });

    return {
      unit,
      department,
      month,
      year,
      documentNumber,
      rows: normalizedRows,
    };
  };

  const submit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();
      if (!id) {
        await AllocationSheetScheduleService.create(payload);
        toast.success("Tạo bảng phân bổ thành công");
      } else {
        await AllocationSheetScheduleService.update(id, payload);
        toast.success("Cập nhật bảng phân bổ thành công");
      }
      navigation("/templates/allocation-sheet-schedule");
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Lưu thất bại");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const htmlContent = getPrintTemplate({
      headerInfo: { ...header, month: normalizeMonth(header.month) },
      rows: derivedRows.map((row, index) => ({ ...row, serialNumber: String(index + 1) })),
      totals,
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "16px",
    },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "transparent",
      color: "#000",
      minWidth: 20,
    },
    inputDate: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "transparent",
      color: "#000",
      width: 36,
      textAlign: "center",
    },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: 1020 },
    th: { border: "1px solid #000", padding: 4, textAlign: "center", fontWeight: "bold", verticalAlign: "middle" },
    td: { border: "1px solid #000", padding: 0, height: 34 },
    inputLeft: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "left",
      fontSize: 14,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
    inputCenter: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "center",
      fontSize: 14,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
    inputRight: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "right",
      fontSize: 14,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
    diagonalCell: {
      position: "relative",
      padding: 0,
      width: "100%",
      height: "100%",
      minHeight: 68,
    },
    diagonalLine: {
      position: "absolute",
      inset: 0,
      background:
        "linear-gradient(135deg, transparent 49.9%, #000 49.9%, #000 50.1%, transparent 50.1%)",
      pointerEvents: "none",
    },
    diagonalTopRight: {
      position: "absolute",
      top: 6,
      right: 8,
      fontWeight: 700,
      fontSize: 14,
      whiteSpace: "nowrap",
    },
    diagonalBottomLeft: {
      position: "absolute",
      left: 8,
      bottom: 6,
      fontSize: 13,
      lineHeight: 1.2,
    },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={10}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Card>
              <SoftBox p={2} style={s.container}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ lineHeight: 1.5 }}>
                    <div>
                      <b>Đơn vị:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 220 }}
                        value={header.unit}
                        onChange={(event) => setHeaderField("unit", event.target.value)}
                      />
                    </div>
                    <div>
                      <b>Bộ phận:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 220 }}
                        value={header.department}
                        onChange={(event) => setHeaderField("department", event.target.value)}
                      />
                    </div>
                  </div>

                  <div style={{ textAlign: "center", fontSize: 15, lineHeight: 1.35 }}>
                    <div>
                      <b>Mẫu số: 07 - VT</b>
                    </div>
                    <div>
                      <i>(Kèm theo Thông tư số 99/2025/TT-BTC</i>
                    </div>
                    <div>
                      <i>ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</i>
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: "center", marginTop: 6 }}>
                  <div style={{ fontWeight: 700, fontSize: 26, textTransform: "uppercase" }}>
                    BẢNG PHÂN BỔ NGUYÊN LIỆU, VẬT LIỆU, CÔNG CỤ, DỤNG CỤ
                  </div>
                  <div style={{ marginTop: 8, fontStyle: "italic" }}>
                    Tháng{" "}
                    <input
                      disabled={containView}
                      style={s.inputDate}
                      value={header.month}
                      onChange={(event) => setHeaderField("month", formatMonth2Digits(event.target.value))}
                      onBlur={() => setHeaderField("month", normalizeMonth(header.month))}
                    />{" "}
                    năm{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDate, width: 56 }}
                      value={header.year}
                      onChange={(event) => setHeaderField("year", event.target.value.replace(/\D/g, "").slice(0, 4))}
                    />
                  </div>
                </div>

                <div style={{ textAlign: "right", marginTop: 8 }}>
                  Số:{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 120, textAlign: "center" }}
                    value={header.documentNumber}
                    onChange={(event) => setHeaderField("documentNumber", event.target.value)}
                  />
                </div>

                <div style={{ overflowX: "auto", border: "1px solid #ccc", marginTop: 10 }}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th rowSpan={2} style={{ ...s.th, width: 60 }}>STT</th>
                        <th rowSpan={2} style={{ ...s.th, width: 300, padding: 0 }}>
                          <div style={s.diagonalCell}>
                            <div style={s.diagonalLine} />
                            <div style={s.diagonalTopRight}>Ghi Có các TK</div>
                            <div style={s.diagonalBottomLeft}>
                              <div>Đối tượng sử dụng</div>
                              <div>(Ghi Nợ các TK)</div>
                            </div>
                          </div>
                        </th>
                        <th colSpan={2} style={{ ...s.th, width: 220 }}>Tài khoản 152</th>
                        <th colSpan={2} style={{ ...s.th, width: 220 }}>Tài khoản 153</th>
                        <th rowSpan={2} style={{ ...s.th, width: 140 }}>Tài khoản 242</th>
                        {!containView ? <th rowSpan={2} style={{ ...s.th, width: 40 }}>#</th> : null}
                      </tr>
                      <tr>
                        <th style={s.th}>Giá hạch toán</th>
                        <th style={s.th}>Giá thực tế</th>
                        <th style={s.th}>Giá hạch toán</th>
                        <th style={s.th}>Giá thực tế</th>
                      </tr>
                      <tr>
                        <th style={s.th}>A</th>
                        <th style={s.th}>B</th>
                        <th style={s.th}>1</th>
                        <th style={s.th}>2</th>
                        <th style={s.th}>3</th>
                        <th style={s.th}>4</th>
                        <th style={s.th}>5</th>
                        {!containView ? <th style={s.th} /> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, index) => (
                        <tr key={`allocation-row-${index}`}>
                          <td style={{ ...s.td, textAlign: "center", fontWeight: 600 }}>{index + 1}</td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputLeft}
                              value={row.debitObject}
                              onChange={(event) => setRowField(index, "debitObject", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputRight}
                              value={row.account152Cost}
                              onChange={(event) => setRowField(index, "account152Cost", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputRight}
                              value={row.account152Actual}
                              onChange={(event) => setRowField(index, "account152Actual", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputRight}
                              value={row.account153Cost}
                              onChange={(event) => setRowField(index, "account153Cost", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputRight}
                              value={row.account153Actual}
                              onChange={(event) => setRowField(index, "account153Actual", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputRight}
                              value={row.account242}
                              onChange={(event) => setRowField(index, "account242", event.target.value)}
                            />
                          </td>
                          {!containView ? (
                            <td style={{ ...s.td, textAlign: "center" }}>
                              <button
                                type="button"
                                onClick={() => removeRow(index)}
                                style={{ border: "none", background: "transparent", color: "#d32f2f", fontWeight: 700, cursor: "pointer" }}
                              >
                                x
                              </button>
                            </td>
                          ) : null}
                        </tr>
                      ))}

                      <tr>
                        <td colSpan={2} style={{ ...s.td, textAlign: "center", fontWeight: 700 }}>Cộng</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{fmtMoney(totals.account152Cost)}</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{fmtMoney(totals.account152Actual)}</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{fmtMoney(totals.account153Cost)}</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{fmtMoney(totals.account153Actual)}</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{fmtMoney(totals.account242)}</td>
                        {!containView ? <td style={s.td} /> : null}
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
                  <SoftButton variant="outlined" color="dark" onClick={() => navigation(-1)}>
                    Trở về
                  </SoftButton>

                  <div style={{ display: "flex", gap: 12 }}>
                    {!containView ? (
                      <SoftButton variant="outlined" color="dark" onClick={addRow}>
                        THÊM DÒNG
                      </SoftButton>
                    ) : null}

                    {containView ? (
                      <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                        In Bảng
                      </SoftButton>
                    ) : null}

                    {!containView ? (
                      <SoftButton variant="gradient" color="dark" onClick={submit} disabled={loadingSubmit}>
                        {id ? "Lưu" : "Tạo"}
                      </SoftButton>
                    ) : null}
                  </div>
                </div>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
